import { classes } from "./classes.js";
import {
  BANK_BG_BAD_SOLID,
  BANK_BG_BRAND,
  BANK_BG_NEUTRAL,
  BANK_FS_META,
  BANK_RADIUS_PILL,
  BANK_TEXT,
  BANK_TEXT_2,
} from "./tokens.js";

/**
 * `F-0b`: a dossier's parcours as 7 or 8 segments (prototype `.track`), replacing `StageStepper` in
 * `workspace.tsx`. `Stepper`/`StagePanel` stay: the console uses them.
 *
 * Each segment is a bar with its label under it and a state: `done`, `current` (with an optional `fill`,
 * 0 to 100, clamped), `todo`, `off` (not on this dossier's path), `paused` and `terminal` (a refusal or a
 * withdrawal, the branch that ends the path). The state is in the DOM (`data-state`) and the current segment is
 * `aria-current="step"`; a paused segment says so in words (`pausedLabel`), not only in stripes.
 */
export type TrackState = "done" | "current" | "todo" | "off" | "paused" | "terminal";

export interface TrackSegment {
  readonly key: string;
  readonly label: string;
  readonly state: TrackState;
  readonly fill?: number;
  /** D2 `.track .seg small`: « 3/15 » after the label of the current segment, in the mono face and the brand ink. */
  readonly count?: string;
  /** The selector a spec and a corridor reach the segment by (`F-0c`, asked by A-1a). */
  readonly testId?: string;
}

export interface TrackProps {
  /** The list's accessible name, from the catalogue. */
  readonly label: string;
  readonly segments: readonly TrackSegment[];
  /** The word a reader hears for a paused segment, from the catalogue. */
  readonly pausedLabel?: string;
  readonly className?: string;
}

const clamp = (n: number): number => Math.min(100, Math.max(0, n));

const fillOf = (segment: TrackSegment): number => {
  if (segment.state === "done" || segment.state === "terminal") return 100;
  if (segment.state === "current") return clamp(segment.fill ?? 50);
  return 0;
};

export function Track({ label, segments, pausedLabel, className }: TrackProps): React.JSX.Element {
  return (
    <ol aria-label={label} data-kit="track" className={classes("m-0 flex list-none gap-1.5 p-0", className)}>
      {segments.map((segment) => (
        <li
          key={segment.key}
          data-state={segment.state}
          data-testid={segment.testId}
          aria-current={segment.state === "current" ? "step" : undefined}
          className="min-w-0 flex-1"
        >
          <div className={classes("h-1.5 overflow-hidden", BANK_RADIUS_PILL, BANK_BG_NEUTRAL)}>
            <div
              className={classes(
                "h-full",
                BANK_RADIUS_PILL,
                segment.state === "terminal" ? BANK_BG_BAD_SOLID : BANK_BG_BRAND,
                segment.state === "paused" && "opacity-60",
              )}
              style={{ width: String(fillOf(segment)) + "%" }}
            />
          </div>
          <div
            className={classes(
              "mt-1.5 break-words",
              BANK_FS_META,
              segment.state === "current" ? classes("font-semibold", BANK_TEXT) : BANK_TEXT_2,
            )}
          >
            {segment.label}
            {segment.count === undefined ? null : (
              <small className="ml-1.5 font-[family-name:var(--hk-mono)] font-medium text-[color:var(--hk-brand-strong)]">
                {segment.count}
              </small>
            )}
            {segment.state === "paused" && pausedLabel !== undefined ? (
              <span className="block font-medium">{pausedLabel}</span>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
